/**
 * Runtime configuration — read at boot, never baked into the build.
 *
 * PROJECT-OVERVIEW §4.1: the Cognito pool id and the two API base URLs must come
 * from a `config.json` served alongside the bundle, so the SAME artifact can be
 * promoted between environments. A `VITE_*` variable is resolved at build time and
 * would force one build per environment — which is exactly what CodePipeline should
 * not have to do.
 *
 * The environment topology is still an open question (§9). Implementing the read now
 * costs nothing and means the answer, whenever it lands, is a deploy concern rather
 * than a code change.
 *
 * ⚠️ `config.json` is public — it is fetched by the browser. Pool ids and API URLs
 * are not secrets; anything that IS a secret does not belong here.
 */
import { POLITICA_PADRAO, type PoliticaDeSenha } from '@/auth/politicaDeSenha'

export interface ConfigAuth {
  region: string
  userPoolId: string
  userPoolClientId: string
  /**
   * Mirrors the pool's own password policy, for the "defina sua senha" screen.
   * It is a courtesy check — Cognito stays authoritative. Defaults to Cognito's
   * defaults; **align it with the real pool when it is provisioned** (§9).
   */
  passwordPolicy: PoliticaDeSenha
}

export interface ConfigRuntime {
  auth: ConfigAuth
  /** Base URLs of the two gateways (§5.5). Story 0.6 consumes these. */
  api: { user: string; adm: string }
}

const CAMINHO = '/config.json'

/**
 * Sobrescrita **apenas de desenvolvimento**, para apontar o `npm run dev` ao pool real
 * do Cognito e às APIs de dev.
 *
 * 🔒 Está no `.gitignore` e não é lida em build de produção. Commitar os ids do pool
 * em `config.json` funcionaria — eles são públicos, o pipeline os publica no
 * CloudFront — mas deixaria uma armadilha silenciosa: se o passo de deploy falhasse
 * em sobrescrever o arquivo, o artefato subiria apontando para o ambiente errado sem
 * nada quebrar visivelmente. Um arquivo não versionado não tem como vazar para lá.
 */
const CAMINHO_LOCAL = '/config.local.json'

/**
 * Tenta a sobrescrita local. Devolve `null` — sem lançar — para qualquer resultado
 * que não seja um JSON de verdade.
 *
 * O cuidado com o `content-type` não é preciosismo: o dev server do Vite responde o
 * `index.html` com status 200 para caminhos que ele não encontra, então "arquivo
 * ausente" chega aqui como HTML bem-sucedido, e sem esta checagem o `.json()`
 * lançaria um erro de parse que parece coisa muito pior do que "não existe".
 */
async function tentarConfigLocal(): Promise<ConfigRuntime | null> {
  try {
    const resposta = await fetch(CAMINHO_LOCAL, { cache: 'no-store' })
    if (!resposta.ok) return null
    if (!(resposta.headers.get('content-type') ?? '').includes('json')) return null
    return normalizar(await resposta.json())
  } catch {
    return null
  }
}

let promessa: Promise<ConfigRuntime> | null = null

function texto(valor: unknown): string {
  return typeof valor === 'string' ? valor : ''
}

function inteiro(valor: unknown, padrao: number): number {
  return typeof valor === 'number' && Number.isInteger(valor) && valor > 0 ? valor : padrao
}

function booleano(valor: unknown, padrao: boolean): boolean {
  return typeof valor === 'boolean' ? valor : padrao
}

function normalizarPolitica(bruto: unknown): PoliticaDeSenha {
  const politica = (bruto ?? {}) as Record<string, unknown>
  return {
    minimoCaracteres: inteiro(politica.minimoCaracteres, POLITICA_PADRAO.minimoCaracteres),
    exigeMaiuscula: booleano(politica.exigeMaiuscula, POLITICA_PADRAO.exigeMaiuscula),
    exigeMinuscula: booleano(politica.exigeMinuscula, POLITICA_PADRAO.exigeMinuscula),
    exigeNumero: booleano(politica.exigeNumero, POLITICA_PADRAO.exigeNumero),
    exigeEspecial: booleano(politica.exigeEspecial, POLITICA_PADRAO.exigeEspecial),
  }
}

function normalizar(bruto: unknown): ConfigRuntime {
  const objeto = (bruto ?? {}) as Record<string, unknown>
  const auth = (objeto.auth ?? {}) as Record<string, unknown>
  const api = (objeto.api ?? {}) as Record<string, unknown>

  return {
    auth: {
      // LGPD: tudo que a SPA conversa vive em sa-east-1.
      region: texto(auth.region) || 'sa-east-1',
      userPoolId: texto(auth.userPoolId),
      userPoolClientId: texto(auth.userPoolClientId),
      passwordPolicy: normalizarPolitica(auth.passwordPolicy),
    },
    api: { user: texto(api.user), adm: texto(api.adm) },
  }
}

/**
 * Loads `config.json` once per page load and memoises it.
 *
 * A failed load is NOT swallowed: the app cannot authenticate without the pool id,
 * and a silent fallback would surface later as an incomprehensible Cognito error.
 * The failed promise is discarded so a retry is possible.
 */
export function carregarConfig(): Promise<ConfigRuntime> {
  if (!promessa) {
    promessa = (async () => {
      if (import.meta.env.DEV) {
        const local = await tentarConfigLocal()
        if (local) return local
      }

      const resposta = await fetch(CAMINHO, { cache: 'no-store' })
      if (!resposta.ok) {
        throw new Error(`${CAMINHO} respondeu ${resposta.status}`)
      }
      return normalizar(await resposta.json())
    })().catch((causa: unknown) => {
      promessa = null
      throw new Error(
        `Não foi possível carregar ${CAMINHO}. A aplicação lê a configuração em runtime (§4.1) e não sobe sem ela.`,
        { cause: causa },
      )
    })
  }
  return promessa
}

/** True once the pool has actually been provisioned (`CONEXÃO`, 14/ago+). */
export function poolConfigurado(config: ConfigRuntime): boolean {
  return config.auth.userPoolId !== '' && config.auth.userPoolClientId !== ''
}

/**
 * Replaces the memoised config, or drops it when passed `null`.
 *
 * Two callers, both outside production code paths: a test that wants a config
 * without stubbing `fetch`, and the MSW layer (story 0.6), which substitutes mock
 * gateway URLs while `api.user` / `api.adm` are still empty.
 */
export function definirConfig(config: ConfigRuntime | null): void {
  promessa = config ? Promise.resolve(config) : null
}
